import React from "react";
import hero from "../../../assets/featuers/heroFeatures.png";
import langs from "../../../assets/featuers/langs.png";
import muliBranches from "../../../assets/featuers/muliBranches.png";
import cutomeize from "../../../assets/featuers/cutomeize.png";
import adds from "../../../assets/featuers/adds.png";
import social from "../../../assets/featuers/socailMedia.png";
import sort from "../../../assets/featuers/sort.png";
import support from "../../../assets/featuers/suport.png";
import tags from "../../../assets/featuers/tags.png";
import upates from "../../../assets/featuers/updates.png";

import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import Iterator from "../../utils/Iterator";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

function FeaturesPage() {
  const { t } = useTranslation();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 40 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6 } 
    },
  };

  const features = [
    { key: "multiLanguages", img: langs },
    { key: "multiBranches", img: muliBranches },
    { key: "designCustomization", img: cutomeize },
    { key: "easyUpdates", img: upates },
    { key: "tagsOffers", img: tags },
    { key: "optionsAddons", img: adds },
    { key: "productSorting", img: sort },
    { key: "socialIntegration", img: social },
    { key: "customerSupport", img: support },
  ];

  return (
    <Layout title={t("pages.features")}>
      {/* Hero Section */}
      <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/5 via-blue-600/3 to-MainBlue/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4 md:space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                  {t("features.heroTitle")}
                </span>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                {t("features.heroDesc")}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-MainBlue/20 to-blue-600/20 rounded-2xl blur-3xl"></div>
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                src={hero}
                alt="Features"
                className="relative z-10 rounded-xl md:rounded-2xl shadow-xl drop-shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-muted via-muted/50 to-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
                {t("features.ourFeatures")}
              </span>
            </h2>
          </motion.div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            <Iterator
              data={features}
              render={(feature, i) => (
                <motion.div
                  variants={item}
                  key={feature.key}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="bg-card rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-6 md:p-8 border border-border/50 overflow-hidden group cursor-pointer"
                >
                  <div className="relative mb-4 md:mb-6 overflow-hidden rounded-lg">
                    <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 to-blue-600/10 group-hover:from-MainBlue/20 group-hover:to-blue-600/20 transition-all duration-300"></div>
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                      src={feature.img}
                      alt={feature.key}
                      className="w-full aspect-square object-cover relative z-10"
                    />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-foreground group-hover:text-MainBlue transition-colors">
                    {t(`features.${feature.key}.title`)}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {t(`features.${feature.key}.desc`)}
                  </p>
                </motion.div>
              )}
            />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-20 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 via-blue-600/5 to-MainBlue/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 md:space-y-8"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                {t("features.ctaTitle")}
              </span>
            </h2>
            <motion.a
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              href="https://wa.me/971528401112?text=Hello%21+I+found+TWHMENU.com+and+I+want+to+create+digital+menu"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-MainBlue to-blue-600 hover:from-blue-600 hover:to-MainBlue text-white px-8 md:px-10 py-4 md:py-5 rounded-lg md:rounded-xl font-semibold text-base md:text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span>{t("features.ctaButton")}</span>
              <motion.div
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.a>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}

export default FeaturesPage;
